import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  listBillingPolicies,
  upsertBillingPolicy,
  listBillableEventTypes,
  listCoverageGaps,
  customerBillingSummary,
  previewBillingPolicy,
} from "@/lib/billing-platform.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute(
  "/_authenticated/app/admin/billing-platform/contracts/$customerId",
)({
  component: ContractPage,
});

type Tier = { min_qty: number; max_qty: number | null; unit_rate_cents: number };

const fmtMoney = (cents: number | null | undefined) =>
  cents == null
    ? "—"
    : (Number(cents) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtDate = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleDateString() : "—";

function ContractPage() {
  const { customerId } = Route.useParams();
  const { tenantId, customers } = useScope();
  const qc = useQueryClient();

  const polFn = useServerFn(listBillingPolicies);
  const etFn = useServerFn(listBillableEventTypes);
  const gapsFn = useServerFn(listCoverageGaps);
  const sumFn = useServerFn(customerBillingSummary);

  const customer = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId],
  );

  const enabled = !!tenantId;

  const { data: polData, isLoading: polLoading } = useQuery({
    queryKey: ["billing-policies", tenantId, customerId],
    enabled,
    queryFn: () => polFn({ data: { tenantId: tenantId!, customerId } }),
  });

  const { data: etData } = useQuery({
    queryKey: ["billable-event-types"],
    enabled,
    queryFn: () => etFn(),
  });

  const { data: gapsData } = useQuery({
    queryKey: ["billing-coverage-gaps", tenantId],
    enabled,
    queryFn: () => gapsFn({ data: { tenantId: tenantId! } }),
  });

  const { data: summary } = useQuery({
    queryKey: ["billing-customer-summary", tenantId, customerId],
    enabled: enabled && !!customerId,
    queryFn: () => sumFn({ data: { tenantId: tenantId!, customerId } }),
  });

  const [editing, setEditing] = useState<any | null>(null);

  const policies = polData?.policies ?? [];
  const eventTypes = etData?.eventTypes ?? [];

  // Coverage: which event types have any activity but no customer-scoped policy
  const policiesByType = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const p of policies as any[]) {
      const arr = m.get(p.event_type) ?? [];
      arr.push(p);
      m.set(p.event_type, arr);
    }
    return m;
  }, [policies]);

  const unpricedTypes = useMemo(() => {
    const gapTypes = new Set<string>(
      ((gapsData?.gaps ?? []) as any[])
        .filter((g) => Number(g.gap_pct) < 100)
        .map((g) => g.event_type as string),
    );
    const result: { code: string; capability: string; unit: string }[] = [];
    for (const et of eventTypes as any[]) {
      if (!policiesByType.has(et.code) && gapTypes.has(et.code)) {
        result.push(et);
      }
    }
    return result;
  }, [eventTypes, policiesByType, gapsData]);

  const groupedByCapability = useMemo(() => {
    const groups = new Map<string, any[]>();
    for (const et of eventTypes as any[]) {
      const arr = groups.get(et.capability) ?? [];
      arr.push(et);
      groups.set(et.capability, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [eventTypes]);

  const activeCount = policies.filter((p: any) => p.active).length;
  const minTotal = policies.reduce(
    (s: number, p: any) => s + Number(p.min_monthly_cents ?? 0),
    0,
  );
  const earliestFrom = policies
    .map((p: any) => p.effective_from)
    .filter(Boolean)
    .sort()[0];
  const latestTo = policies
    .map((p: any) => p.effective_to)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];

  return (
    <>
      <PageHeader
        title={`Billing contract — ${customer?.name ?? "Customer"}`}
        description="How this customer is billed across event types. Capabilities emit; policies price; ledger records."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/app/admin/billing-platform">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Link>
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setEditing({
                  __new: true,
                  tenant_id: tenantId,
                  customer_id: customerId,
                  event_type: "",
                  name: "",
                  description: "",
                  unit_rate_cents: null,
                  tiers: [],
                  min_monthly_cents: null,
                  free_allowance: 0,
                  priority: 100,
                  active: true,
                })
              }
              disabled={!tenantId}
            >
              <Plus className="h-4 w-4 mr-1" /> Add policy
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 py-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active policies" value={String(activeCount)} sub={`${policies.length} total`} />
        <StatCard
          label="Effective coverage"
          value={earliestFrom ? fmtDate(earliestFrom) : "—"}
          sub={latestTo ? `through ${fmtDate(latestTo)}` : "no end dates"}
        />
        <StatCard
          label="Monthly minimums"
          value={fmtMoney(minTotal)}
          sub="Sum across policies"
        />
        <StatCard
          label="This month billed"
          value={summary ? fmtMoney(summary.totalCurrentCents) : "—"}
          sub={
            summary
              ? `${summary.deltaCents >= 0 ? "+" : ""}${fmtMoney(summary.deltaCents)} vs prior`
              : "—"
          }
        />
      </div>

      {unpricedTypes.length > 0 && (
        <div className="px-4 sm:px-6">
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Event types with activity but no policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {unpricedTypes.map((t) => (
                  <Button
                    key={t.code}
                    size="sm"
                    variant="outline"
                    className="font-mono text-xs"
                    onClick={() =>
                      setEditing({
                        __new: true,
                        tenant_id: tenantId,
                        customer_id: customerId,
                        event_type: t.code,
                        name: `${t.code} — ${customer?.name ?? "customer"}`,
                        description: "",
                        unit_rate_cents: null,
                        tiers: [],
                        min_monthly_cents: null,
                        free_allowance: 0,
                        priority: 100,
                        active: true,
                      })
                    }
                  >
                    <Plus className="h-3 w-3 mr-1" /> {t.code}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {groupedByCapability.map(([capability, types]) => {
          const capPolicies = (policies as any[]).filter((p) =>
            types.some((t: any) => t.code === p.event_type),
          );
          if (capPolicies.length === 0) return null;
          return (
            <Card key={capability}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
                  {capability}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Policy</th>
                      <th className="text-left p-3">Event / Unit</th>
                      <th className="text-left p-3">Rate</th>
                      <th className="text-right p-3">Free</th>
                      <th className="text-right p-3">Min/mo</th>
                      <th className="text-left p-3">Effective</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {capPolicies.map((p: any) => {
                      const et = (eventTypes as any[]).find((e) => e.code === p.event_type);
                      const tiers = (p.tiers ?? []) as Tier[];
                      const rateLabel =
                        tiers && tiers.length > 0
                          ? `${tiers.length}-tier (${fmtMoney(tiers[0].unit_rate_cents)}–${fmtMoney(
                              tiers[tiers.length - 1].unit_rate_cents,
                            )} /${et?.unit ?? "unit"})`
                          : p.unit_rate_cents != null
                            ? `${fmtMoney(p.unit_rate_cents)} /${et?.unit ?? "unit"}`
                            : "—";
                      return (
                        <tr key={p.id} className="border-t">
                          <td className="p-3">
                            <div className="font-medium">{p.name}</div>
                            {p.description && (
                              <div className="text-xs text-muted-foreground">{p.description}</div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs">
                            {p.event_type}
                            <div className="text-[10px] text-muted-foreground">
                              per {et?.unit ?? "unit"}
                            </div>
                          </td>
                          <td className="p-3 text-xs">{rateLabel}</td>
                          <td className="p-3 text-right tabular-nums">
                            {Number(p.free_allowance ?? 0) || "—"}
                          </td>
                          <td className="p-3 text-right tabular-nums">
                            {fmtMoney(p.min_monthly_cents)}
                          </td>
                          <td className="p-3 text-xs">
                            {fmtDate(p.effective_from)} → {fmtDate(p.effective_to)}
                          </td>
                          <td className="p-3">
                            <Badge variant={p.active ? "default" : "outline"} className="text-[10px]">
                              {p.active ? "active" : "inactive"}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })}

        {!polLoading && policies.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No policies yet. Add a policy for any event type this customer generates.
            </CardContent>
          </Card>
        )}
      </div>

      {editing && (
        <PolicyEditor
          policy={editing}
          eventTypes={eventTypes as any[]}
          tenantId={tenantId!}
          customerId={customerId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["billing-policies"] });
            qc.invalidateQueries({ queryKey: ["billing-coverage-gaps"] });
            toast.success("Policy saved");
          }}
        />
      )}
    </>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
      </CardContent>
    </Card>
  );
}

function PolicyEditor({
  policy,
  eventTypes,
  tenantId,
  customerId,
  onClose,
  onSaved,
}: {
  policy: any;
  eventTypes: any[];
  tenantId: string;
  customerId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const upFn = useServerFn(upsertBillingPolicy);
  const prevFn = useServerFn(previewBillingPolicy);

  const [eventType, setEventType] = useState<string>(policy.event_type ?? "");
  const [name, setName] = useState<string>(policy.name ?? "");
  const [description, setDescription] = useState<string>(policy.description ?? "");
  const [unitRateDollars, setUnitRateDollars] = useState<string>(
    policy.unit_rate_cents != null ? (Number(policy.unit_rate_cents) / 100).toString() : "",
  );
  const initialTiers: Tier[] = Array.isArray(policy.tiers) ? policy.tiers : [];
  const [tiers, setTiers] = useState<Tier[]>(initialTiers);
  const [useTiers, setUseTiers] = useState<boolean>(initialTiers.length > 0);
  const [minMonthlyDollars, setMinMonthlyDollars] = useState<string>(
    policy.min_monthly_cents != null ? (Number(policy.min_monthly_cents) / 100).toString() : "",
  );
  const [freeAllowance, setFreeAllowance] = useState<string>(
    String(policy.free_allowance ?? 0),
  );
  const [effectiveFrom, setEffectiveFrom] = useState<string>(
    policy.effective_from ? String(policy.effective_from).slice(0, 10) : "",
  );
  const [effectiveTo, setEffectiveTo] = useState<string>(
    policy.effective_to ? String(policy.effective_to).slice(0, 10) : "",
  );
  const [active, setActive] = useState<boolean>(policy.active ?? true);

  const et = eventTypes.find((e) => e.code === eventType);
  const unit = et?.unit ?? "unit";

  const tiersForPreview: Tier[] | null = useTiers && tiers.length > 0 ? tiers : null;
  const unitRateCents = unitRateDollars ? Math.round(Number(unitRateDollars) * 100) : null;
  const minMonthlyCents = minMonthlyDollars ? Math.round(Number(minMonthlyDollars) * 100) : null;

  const previewEnabled = !!eventType && (tiersForPreview !== null || unitRateCents != null);

  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: [
      "billing-policy-preview",
      tenantId,
      customerId,
      eventType,
      unitRateCents,
      JSON.stringify(tiersForPreview),
      minMonthlyCents,
      freeAllowance,
    ],
    enabled: previewEnabled,
    queryFn: () =>
      prevFn({
        data: {
          tenantId,
          customerId,
          eventType,
          unitRateCents,
          tiers: tiersForPreview,
          minMonthlyCents,
          freeAllowance: Number(freeAllowance) || 0,
          windowDays: 90,
        },
      }),
  });

  const statement = useMemo(() => {
    if (!eventType) return "Pick an event type to compose this policy.";
    const rateText =
      tiersForPreview && tiersForPreview.length > 0
        ? `${tiersForPreview.length}-tier pricing`
        : unitRateCents != null
          ? `${fmtMoney(unitRateCents)} per ${unit}`
          : "no rate set";
    const freeText = Number(freeAllowance) > 0 ? ` after first ${freeAllowance} ${unit}s` : "";
    const minText = minMonthlyCents ? `, ${fmtMoney(minMonthlyCents)} monthly minimum` : "";
    return `Charge ${rateText} for every ${eventType}${freeText}${minText}.`;
  }, [eventType, tiersForPreview, unitRateCents, freeAllowance, minMonthlyCents, unit]);

  const save = async () => {
    if (!eventType || !name) {
      toast.error("Event type and name are required");
      return;
    }
    await runServerFn(
      () =>
        upFn({
          data: {
            id: policy.__new ? undefined : policy.id,
            tenantId,
            customerId,
            eventType,
            name,
            description: description || null,
            unitRateCents: useTiers ? null : unitRateCents,
            tiers: useTiers && tiers.length > 0 ? tiers : null,
            minMonthlyCents,
            freeAllowance: Number(freeAllowance) || 0,
            priority: policy.priority ?? 100,
            active,
            effectiveFrom: effectiveFrom || null,
            effectiveTo: effectiveTo || null,
          },
        }),
      "Save policy",
    );
    onSaved();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{policy.__new ? "New billing policy" : "Edit billing policy"}</DialogTitle>
        </DialogHeader>

        {/* Policy Surface — Statement */}
        <div className="rounded-lg border bg-card p-3 text-sm">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Statement</div>
          <div className="mt-1">{statement}</div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <Label>Event type</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick an event type" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((e) => (
                    <SelectItem key={e.code} value={e.code}>
                      <span className="font-mono text-xs">{e.code}</span>{" "}
                      <span className="text-muted-foreground">/ {e.unit}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                Scope
              </div>
              <div className="text-xs">Customer-scoped · trigger: every {eventType || "event"}</div>
            </div>

            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="rounded border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="m-0">Tiered pricing</Label>
                <Switch checked={useTiers} onCheckedChange={setUseTiers} />
              </div>
              {!useTiers ? (
                <div>
                  <Label>Flat unit rate (USD per {unit})</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={unitRateDollars}
                    onChange={(e) => setUnitRateDollars(e.target.value)}
                    placeholder="0.50"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  {tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3">
                        <Label className="text-[10px]">Min qty</Label>
                        <Input
                          type="number"
                          value={t.min_qty}
                          onChange={(e) =>
                            setTiers((rs) =>
                              rs.map((r, idx) =>
                                idx === i ? { ...r, min_qty: Number(e.target.value) } : r,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px]">Max qty (blank = ∞)</Label>
                        <Input
                          type="number"
                          value={t.max_qty ?? ""}
                          onChange={(e) =>
                            setTiers((rs) =>
                              rs.map((r, idx) =>
                                idx === i
                                  ? {
                                      ...r,
                                      max_qty: e.target.value === "" ? null : Number(e.target.value),
                                    }
                                  : r,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="col-span-5">
                        <Label className="text-[10px]">Rate (USD per {unit})</Label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={(t.unit_rate_cents / 100).toString()}
                          onChange={(e) =>
                            setTiers((rs) =>
                              rs.map((r, idx) =>
                                idx === i
                                  ? {
                                      ...r,
                                      unit_rate_cents: Math.round(Number(e.target.value) * 100),
                                    }
                                  : r,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setTiers((rs) => rs.filter((_, idx) => idx !== i))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const last = tiers[tiers.length - 1];
                      const nextMin = last ? (last.max_qty ?? last.min_qty) + 1 : 0;
                      setTiers([
                        ...tiers,
                        { min_qty: nextMin, max_qty: null, unit_rate_cents: 0 },
                      ]);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add tier
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Free allowance ({unit}/mo)</Label>
                <Input
                  type="number"
                  value={freeAllowance}
                  onChange={(e) => setFreeAllowance(e.target.value)}
                />
              </div>
              <div>
                <Label>Monthly minimum (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={minMonthlyDollars}
                  onChange={(e) => setMinMonthlyDollars(e.target.value)}
                />
              </div>
              <div>
                <Label>Effective from</Label>
                <Input
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  disabled
                  title="Effective dating is read-only in this editor; managed by policy versioning."
                />
              </div>
              <div>
                <Label>Effective to</Label>
                <Input
                  type="date"
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                  disabled
                  title="Effective dating is read-only in this editor; managed by policy versioning."
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded border p-3">
              <div>
                <Label className="m-0">Active</Label>
                <div className="text-xs text-muted-foreground">
                  Inactive policies do not price new events.
                </div>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          </div>

          {/* Preview Impact — backend computed */}
          <div className="space-y-3">
            <div className="rounded-lg border bg-card">
              <div className="border-b px-4 py-2.5">
                <h3 className="text-sm font-medium">Preview impact (90d)</h3>
                <p className="text-xs text-muted-foreground">
                  Computed by the billing platform against real billable events.
                </p>
              </div>
              {!previewEnabled ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Set an event type and a rate to preview.
                </div>
              ) : previewLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Running preview…</div>
              ) : !preview ? (
                <div className="p-4 text-sm text-muted-foreground">No data.</div>
              ) : (
                <div>
                  <div className="grid grid-cols-3 gap-px bg-border border-b">
                    <Metric label="Events" value={preview.totalEvents.toLocaleString()} />
                    <Metric label="Total qty" value={preview.totals.quantity.toLocaleString()} />
                    <Metric label="Charged" value={fmtMoney(preview.totals.charged_cents)} />
                  </div>
                  <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                    Subtotal {fmtMoney(preview.totals.subtotal_cents)} → after monthly minimum{" "}
                    {fmtMoney(preview.totals.charged_cents)} ·{" "}
                    {preview.unpricedEvents} unpriced events would remain
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] text-muted-foreground uppercase">
                      <tr>
                        <th className="text-left p-2">Month</th>
                        <th className="text-right p-2">Events</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Subtotal</th>
                        <th className="text-right p-2">Charged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.months.map((m: any) => (
                        <tr key={m.month} className="border-t">
                          <td className="p-2 font-mono text-xs">{m.month}</td>
                          <td className="p-2 text-right tabular-nums">{m.events}</td>
                          <td className="p-2 text-right tabular-nums">{m.quantity}</td>
                          <td className="p-2 text-right tabular-nums">
                            {fmtMoney(m.subtotal_cents)}
                          </td>
                          <td className="p-2 text-right tabular-nums font-medium">
                            {fmtMoney(m.min_applied_cents)}
                          </td>
                        </tr>
                      ))}
                      {preview.months.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-3 text-center text-muted-foreground text-xs">
                            No events for this type in the last 90 days.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground mb-1">How this works</div>
              Billing observes operational truth. Capabilities emit billable events; this policy
              prices them and writes a frozen <code>policy_snapshot</code> onto every ledger entry.
              Edits never silently re-price historical charges.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save}>Save policy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}